// Settings → Account (moderation phase 2 arc 4): Account Status, appeals,
// "Your reports" and Download my data, over /api/account/*. A suspended or
// terminated person reaches all of it (server allowlists).
//
// Auth headers are attached by hand (AuthService.getAuthHeaders), as every
// service does: the fetch interceptor refreshes tokens but never adds them.

import { API_BASE } from '../config/api';
import AuthService from './authService';

async function readJson(response) {
  try {
    return await response.json();
  } catch {
    return {};
  }
}

class AccountStatusService {
  static async getStatus() {
    try {
      const response = await fetch(`${API_BASE}/api/account/status`, { headers: AuthService.getAuthHeaders() });
      const body = await readJson(response);
      if (response.ok && body.success) return { success: true, status: body };
      return { success: false, error: body.error || null };
    } catch (error) {
      console.error('Error loading account status:', error);
      return { success: false, error: null };
    }
  }

  static async getReports() {
    try {
      const response = await fetch(`${API_BASE}/api/account/reports`, { headers: AuthService.getAuthHeaders() });
      const body = await readJson(response);
      if (response.ok && body.success) return { success: true, reports: body.reports || [] };
      return { success: false, error: body.error || null };
    } catch (error) {
      console.error('Error loading your reports:', error);
      return { success: false, error: null };
    }
  }

  /** `image` is an optional data URI. The server's refusal text comes back as `error`. */
  static async fileAppeal(actionId, body, image = null) {
    try {
      const response = await fetch(`${API_BASE}/api/account/appeals`, {
        method: 'POST',
        headers: AuthService.getJsonHeaders(),
        body: JSON.stringify({ action_id: actionId, body, ...(image ? { image_base64: image } : {}) })
      });
      const result = await readJson(response);
      if (response.ok && result.success) return { success: true, appealId: result.appeal_id };
      return { success: false, code: result.code || null, error: result.error || null };
    } catch (error) {
      console.error('Error filing an appeal:', error);
      return { success: false, error: null };
    }
  }

  /** The image on the person's own appeal, as an object URL the caller revokes. */
  static async getAppealImageUrl(appealId) {
    try {
      const response = await fetch(`${API_BASE}/api/account/appeals/${encodeURIComponent(appealId)}/image`, {
        headers: AuthService.getAuthHeaders()
      });
      if (!response.ok) return null;
      return URL.createObjectURL(await response.blob());
    } catch (error) {
      console.error('Error loading an appeal image:', error);
      return null;
    }
  }

  /**
   * Download my data: fetches the zip and hands it to the browser as a file.
   * Once a day: a refusal returns { code: 'export_limited', availableAt }.
   */
  static async downloadData() {
    try {
      const response = await fetch(`${API_BASE}/api/account/export`, { headers: AuthService.getAuthHeaders() });
      if (!response.ok) {
        const body = await readJson(response);
        return { success: false, code: body.code || null, availableAt: body.available_at || null, error: body.error || null };
      }
      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const filename = /filename="([^"]+)"/.exec(disposition)?.[1] || 'liveyourfun-data.zip';
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      return { success: true };
    } catch (error) {
      console.error('Error downloading your data:', error);
      return { success: false, error: null };
    }
  }
}

export default AccountStatusService;
